import clsx from "clsx";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ChevronDownIcon } from "@/assets/icons/general/ChevronDown";
import { Button } from "@/components/ui/buttons/Button/Button";
import { Typography } from "@/components/ui/text/Typography/Typography";
import UploadsPageFileUploadRow from "@/components/uploads/modal/UploadsPageFileUploadRow";
import useBackgroundProfileUploads from "@/components/uploads/useBackgroundProfileUploads";
import { ProfileUploadsContext } from "@/data/uploads/uploads.context";

const UploadsPageFilesUploadMinimizedModal = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation();
  const { currentUploadBatch, showMinimizedWindow, uploadQueue } = ProfileUploadsContext();
  useBackgroundProfileUploads();

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };
  const displayBatches = uploadQueue?.filter((batch) => batch.id !== currentUploadBatch?.id) || [];

  useEffect(() => {
    setIsExpanded(false);
  }, [uploadQueue?.length]);

  if (!showMinimizedWindow || uploadQueue.length === 0) {
    return null;
  }

  return (
    <div className="fixed right-4 bottom-4 z-50 min-w-[400px]">
      <div className="flex flex-col rounded-xs border border-elevation-outline-1 bg-elevation-surface-1 shadow-5 transition-all duration-300 ease-in-out">
        <Button
          onPress={toggleExpanded}
          className="!bg-elevation-background flex w-full items-center justify-between gap-2 p-3 transition-colors duration-200 hover:bg-elevation-surface-2"
        >
          <div className="flex flex-col items-start">
            <Typography
              size="body-3"
              className="text-text-default-primary"
            >
              {t("uploads.head.title")}
            </Typography>
            <Typography
              size="body-4"
              className="text-text-default-secondary"
            >
              {currentUploadBatch?.uploadedMediaIds.length} / {currentUploadBatch?.files?.length} files uploaded
            </Typography>
          </div>
          <div className={clsx("transition-transform duration-300", !isExpanded && "rotate-180")}>
            <ChevronDownIcon className="h-6 w-6 text-text-default-primary" />
          </div>
        </Button>
        <div
          className={clsx(
            "overflow-hidden overflow-y-scroll transition-all duration-300 ease-in-out",
            isExpanded ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0",
          )}
        >
          <div className="space-y-4 p-2">
            {[currentUploadBatch, ...displayBatches].map((batch, batchIndex) => (
              <div
                key={batch?.id}
                className="border-elevation-outline-2 border-b pb-2 last:border-b-0"
              >
                <div className="mb-2">
                  <Typography
                    size="body-4"
                    className="text-text-default-secondary"
                  >
                    Batch {batchIndex + 1}
                  </Typography>
                  {!!batch?.jobTitle && (
                    <Typography
                      size="body-4"
                      className="text-text-default-secondary"
                    >
                      Job: {batch.jobTitle}
                    </Typography>
                  )}
                  {!!batch?.tags?.length && (
                    <Typography
                      size="body-4"
                      className="text-text-default-secondary"
                    >
                      Tags: {batch.tags.map((tag) => tag.name).join(", ")}
                    </Typography>
                  )}
                </div>
                {batch?.files.map((file, index) => (
                  <UploadsPageFileUploadRow
                    key={index}
                    file={file.file}
                    status={file.status}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadsPageFilesUploadMinimizedModal;
